import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, Package, Search, Check, RefreshCw, History, AlertTriangle, Upload, Download, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import BarcodeInput from '@/components/BarcodeInput';
import ScanFeedbackOverlay from '@/components/ScanFeedbackOverlay';
import apiClient from '@/services/apiClient';

const SFGDesignTransfer = () => {
  const navigate = useNavigate();
  const targetSkuRef = useRef(null);
  const bundleInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);

  // State
  const [mode, setMode] = useState('single'); // 'single' or 'bulk'
  const [step, setStep] = useState('sku'); // 'sku', 'bundle', or 'confirm'
  const [targetSkuInput, setTargetSkuInput] = useState('');
  const [targetSku, setTargetSku] = useState(null);
  const [bundleInput, setBundleInput] = useState('');
  const [bundleDetails, setBundleDetails] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(null);
  const [errors, setErrors] = useState({});
  const [transferHistory, setTransferHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [lastTransfer, setLastTransfer] = useState(null);

  // Bulk transfer state
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkRows, setBulkRows] = useState([]); // [{target_barcode, bundle_code}]
  const [bulkParseError, setBulkParseError] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkResults, setBulkResults] = useState(null); // {success_count, failure_count, results}

  // Auto-focus
  useEffect(() => {
    if (step === 'sku' && targetSkuRef.current) {
      targetSkuRef.current.focus();
    } else if (step === 'bundle' && bundleInputRef.current) {
      bundleInputRef.current.focus();
    }
  }, [step]);

  const showFeedback = (type, message) => {
    setScanFeedback({ type, message });
    setTimeout(() => setScanFeedback(null), 350);
  };

  const handleTargetSkuScan = async (value) => {
    if (!value || value.trim() === '') {
      setErrors({ ...errors, sku: 'Target SKU is required' });
      showFeedback('error', 'SKU Required');
      return;
    }

    try {
      const response = await apiClient.get(`/wms/sfg-design-transfer/lookup-sku/${encodeURIComponent(value.trim())}`);
      
      if (!response.data.found) {
        setErrors({ ...errors, sku: response.data.message });
        showFeedback('error', 'SKU Not Found');
        setTargetSkuInput('');
        return;
      }

      setTargetSku(response.data.sku);
      setTargetSkuInput('');
      setErrors({ ...errors, sku: null });
      showFeedback('success', `SKU: ${response.data.sku.design_name}`);
      setStep('bundle');
      
      toast.success(`Target SKU: ${response.data.sku.design_name} / ${response.data.sku.shade}`);
    } catch (error) {
      console.error('SKU lookup error:', error);
      setErrors({ ...errors, sku: 'Failed to lookup SKU' });
      showFeedback('error', 'Lookup Error');
      setTargetSkuInput('');
    }
  };

  const handleBundleScan = async (value) => {
    if (!value || value.trim() === '') {
      setErrors({ ...errors, bundle: 'Bundle code is required' });
      showFeedback('error', 'Bundle Required');
      return;
    }

    try {
      const response = await apiClient.get(`/wms/sfg-design-transfer/lookup-bundle/${encodeURIComponent(value.trim())}`);
      
      if (!response.data.found) {
        setErrors({ ...errors, bundle: response.data.message });
        showFeedback('error', 'Bundle Not Found');
        setBundleInput('');
        return;
      }

      setBundleDetails(response.data.bundle);
      setBundleInput('');
      setErrors({ ...errors, bundle: null });
      showFeedback('success', `Bundle: ${response.data.bundle.usn_count} pieces`);
      setShowConfirmDialog(true);
      
    } catch (error) {
      console.error('Bundle lookup error:', error);
      setErrors({ ...errors, bundle: 'Failed to lookup bundle' });
      showFeedback('error', 'Lookup Error');
      setBundleInput('');
    }
  };

  const handleConfirmTransfer = async () => {
    if (!targetSku || !bundleDetails) {
      toast.error('Missing target SKU or bundle details');
      return;
    }

    setIsTransferring(true);
    setShowConfirmDialog(false);

    try {
      const response = await apiClient.post('/wms/sfg-design-transfer/transfer-bundle', {
        bundle_code: bundleDetails.bundle_code,
        target_barcode: targetSku.barcode
      });

      if (response.data.success) {
        setLastTransfer({
          bundle_code: response.data.bundle_code,
          original_design: response.data.original_design,
          target_design: response.data.target_design,
          usn_count: response.data.usn_count,
          timestamp: new Date()
        });
        
        showFeedback('success', 'Transfer Complete!');
        toast.success(`Transferred ${response.data.usn_count} pieces from "${response.data.original_design || 'Unknown'}" to "${response.data.target_design}"`);
        
        if (response.data.labels_need_reprint) {
          toast.warning('Labels were already printed for this bundle. New labels will reflect the updated design when reprinted.');
        }
        
        // Reset for next transfer
        setBundleDetails(null);
        setBundleInput('');
        
        // Focus back on bundle input for next scan
        setTimeout(() => {
          if (bundleInputRef.current) {
            bundleInputRef.current.focus();
          }
        }, 100);
      }
    } catch (error) {
      console.error('Transfer error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to transfer';
      showFeedback('error', 'Transfer Failed');
      toast.error(errorMsg);
    } finally {
      setIsTransferring(false);
    }
  };

  const handleChangeSku = () => {
    setStep('sku');
    setTargetSku(null);
    setBundleDetails(null);
  };

  const handleNewTransfer = () => {
    setStep('sku');
    setTargetSkuInput('');
    setTargetSku(null);
    setBundleInput('');
    setBundleDetails(null);
    setLastTransfer(null);
    setErrors({});
  };

  const loadHistory = async () => {
    try {
      const response = await apiClient.get('/wms/sfg-design-transfer/transfer-history?limit=50');
      setTransferHistory(response.data.transfers || []);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load transfer history');
    }
  };

  const handleShowHistory = () => {
    setShowHistory(true);
    loadHistory();
  };

  // --- Bulk Transfer Handlers ---
  const handleDownloadTemplate = () => {
    const csv = 'target_barcode,bundle_code\nSKU0001,BUN000123/25-26\nSKU0002,BUN000124/25-26\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sfg_bulk_design_transfer_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const parseCsv = (text) => {
    // Simple CSV parser supporting quoted fields
    const rows = [];
    const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return rows;

    const parseLine = (line) => {
      const fields = [];
      let cur = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
          if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
          else if (ch === '"') { inQuotes = false; }
          else { cur += ch; }
        } else {
          if (ch === '"') inQuotes = true;
          else if (ch === ',') { fields.push(cur.trim()); cur = ''; }
          else cur += ch;
        }
      }
      fields.push(cur.trim());
      return fields;
    };

    const header = parseLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));
    const tIdx = header.indexOf('target_barcode');
    const bIdx = header.indexOf('bundle_code');
    if (tIdx === -1 || bIdx === -1) {
      throw new Error('CSV header must include "target_barcode" and "bundle_code" columns');
    }
    for (let i = 1; i < lines.length; i++) {
      const f = parseLine(lines[i]);
      const target_barcode = (f[tIdx] || '').trim();
      const bundle_code = (f[bIdx] || '').trim();
      if (!target_barcode && !bundle_code) continue;
      rows.push({ target_barcode, bundle_code });
    }
    return rows;
  };

  const handleBulkFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkFileName(file.name);
    setBulkParseError('');
    setBulkRows([]);
    setBulkResults(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parseCsv(ev.target.result);
        if (parsed.length === 0) {
          setBulkParseError('CSV is empty or has no valid rows');
          return;
        }
        if (parsed.length > 5000) {
          setBulkParseError(`Too many rows (${parsed.length}). Max 5000 per upload.`);
          return;
        }
        setBulkRows(parsed);
      } catch (err) {
        setBulkParseError(err.message || 'Failed to parse CSV');
      }
    };
    reader.onerror = () => setBulkParseError('Failed to read file');
    reader.readAsText(file);
  };

  const handleBulkClear = () => {
    setBulkFileName('');
    setBulkRows([]);
    setBulkParseError('');
    setBulkResults(null);
    if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
  };

  const handleBulkTransfer = async () => {
    if (bulkRows.length === 0) return;
    setBulkProcessing(true);
    setBulkResults(null);
    try {
      const response = await apiClient.post(
        '/wms/sfg-design-transfer/bulk-transfer',
        { rows: bulkRows },
        { timeout: 600000 }
      );
      setBulkResults(response.data);
      const { success_count = 0, failure_count = 0, total_usns_transferred = 0 } = response.data || {};
      if (failure_count === 0) {
        toast.success(`All ${success_count} bundles transferred (${total_usns_transferred} pieces)`);
      } else if (success_count === 0) {
        toast.error(`All ${failure_count} rows failed`);
      } else {
        toast.warning(`${success_count} transferred, ${failure_count} failed (${total_usns_transferred} pieces)`);
      }
    } catch (error) {
      console.error('Bulk transfer error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Bulk transfer failed';
      toast.error(errorMsg);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleDownloadResults = () => {
    if (!bulkResults || !bulkResults.results) return;
    const csvEscape = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ['row', 'target_barcode', 'bundle_code', 'status', 'usn_count', 'original_design', 'target_design', 'error_code', 'message'];
    const lines = [header.join(',')];
    for (const r of bulkResults.results) {
      lines.push([
        r.row,
        csvEscape(r.target_barcode),
        csvEscape(r.bundle_code),
        r.success ? 'success' : 'failed',
        r.usn_count || '',
        csvEscape(r.original_design || ''),
        csvEscape(r.target_design || ''),
        csvEscape(r.error_code || ''),
        csvEscape(r.message || ''),
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    link.href = url;
    link.download = `sfg_bulk_transfer_results_${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="sfg-design-transfer">
      <ScanFeedbackOverlay feedback={scanFeedback} />
      
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/wms/sfg')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <ArrowRightLeft className="w-6 h-6" />
                SFG Design Transfer
              </h1>
              <p className="text-sm text-[#E8DCC8]">Transfer entire bundle to a new design</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={handleShowHistory}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="history-btn"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
            {targetSku && (
              <Button 
                onClick={handleNewTransfer}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                New Transfer
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        <Tabs value={mode} onValueChange={setMode} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4" data-testid="sfg-design-transfer-tabs">
            <TabsTrigger value="single" data-testid="tab-single">
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Single Transfer
            </TabsTrigger>
            <TabsTrigger value="bulk" data-testid="tab-bulk">
              <Upload className="w-4 h-4 mr-2" />
              Bulk Transfer (CSV)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="single" className="space-y-6 mt-0">
        {/* Step 1: Enter Target SKU */}
        {step === 'sku' && (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                <span className="bg-[#2D5F4F] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                Enter Target SKU (New Design)
              </CardTitle>
              <CardDescription>
                Scan or enter the SKU barcode of the design you want to transfer the bundle TO
              </CardDescription>
            </CardHeader>
            <CardContent>
              <BarcodeInput
                ref={targetSkuRef}
                value={targetSkuInput}
                onChange={setTargetSkuInput}
                onScan={handleTargetSkuScan}
                placeholder="Scan or enter target SKU barcode"
                error={errors.sku}
                data-testid="target-sku-input"
              />
            </CardContent>
          </Card>
        )}

        {/* Step 2: Scan Bundle (after SKU is selected) */}
        {step === 'bundle' && targetSku && (
          <>
            {/* Target SKU Info */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-green-100 p-3 rounded-lg">
                      <Package className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-green-600 font-medium">Target Design (New)</p>
                      <p className="text-xl font-bold text-green-800">{targetSku.design_name}</p>
                      <p className="text-sm text-green-600">
                        Shade: {targetSku.shade || '-'} | Size: {targetSku.size || '-'}
                      </p>
                      <p className="text-xs text-green-500 font-mono mt-1">{targetSku.barcode}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-green-600">MRP</p>
                    <p className="text-xl font-bold text-green-800">₹{targetSku.mrp || 0}</p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleChangeSku}
                      className="text-green-600 hover:text-green-700 mt-1"
                    >
                      Change SKU
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scan Bundle Card */}
            <Card className="border-[#E5DFD0]">
              <CardHeader className="pb-3">
                <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                  <span className="bg-[#2D5F4F] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                  Scan Bundle to Transfer
                </CardTitle>
                <CardDescription>
                  Scan the bundle code. ALL pieces in the bundle will be transferred to <strong>{targetSku.design_name}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <BarcodeInput
                  ref={bundleInputRef}
                  value={bundleInput}
                  onChange={setBundleInput}
                  onScan={handleBundleScan}
                  placeholder="Scan bundle code (e.g., BUN044897/25-26)"
                  error={errors.bundle}
                  disabled={isTransferring}
                  data-testid="bundle-input"
                />
                {isTransferring && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Transferring...
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Last Transfer Success */}
            {lastTransfer && (
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-emerald-100 p-3 rounded-full">
                      <Check className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-emerald-600 font-medium">Last Transfer Successful</p>
                      <p className="text-lg font-bold text-emerald-800">{lastTransfer.bundle_code}</p>
                      <p className="text-sm text-emerald-600">
                        {lastTransfer.usn_count} pieces: {lastTransfer.original_design || 'Unknown'} → {lastTransfer.target_design}
                      </p>
                    </div>
                    <div className="text-right text-xs text-emerald-500">
                      {lastTransfer.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Instructions */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-blue-800 mb-2">How SFG Design Transfer Works</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
              <li>Scan the <strong>Target SKU</strong> (the new design barcode)</li>
              <li>Scan the <strong>Bundle Code</strong> to transfer</li>
              <li>Confirm the transfer - ALL pieces in the bundle will be updated</li>
              <li>When the bundle is scanned for <strong>Main Label Printing</strong>, the NEW design will be printed</li>
            </ol>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="bulk" className="space-y-6 mt-0">
            {/* Upload Card */}
            <Card className="border-[#E5DFD0]">
              <CardHeader>
                <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                  <span className="bg-[#2D5F4F] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">
                    <Upload className="w-4 h-4" />
                  </span>
                  Bulk Design Transfer via CSV
                </CardTitle>
                <CardDescription>
                  Upload a CSV with columns <code>target_barcode</code>, <code>bundle_code</code>. Each row's bundle will be transferred to its target SKU.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={handleDownloadTemplate}
                    data-testid="bulk-download-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV Template
                  </Button>
                  <input
                    ref={bulkFileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleBulkFileChange}
                    className="hidden"
                    data-testid="bulk-file-input"
                  />
                  <Button
                    onClick={() => bulkFileInputRef.current?.click()}
                    className="bg-[#2D5F4F] hover:bg-[#244C40] text-white"
                    disabled={bulkProcessing}
                    data-testid="bulk-choose-file-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Choose CSV File
                  </Button>
                  {bulkFileName && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <FileText className="w-4 h-4" />
                      <span className="font-medium">{bulkFileName}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleBulkClear}
                        disabled={bulkProcessing}
                        data-testid="bulk-clear-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {bulkParseError && (
                  <Alert className="bg-red-50 border-red-300">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-700">
                      {bulkParseError}
                    </AlertDescription>
                  </Alert>
                )}

                {bulkRows.length > 0 && !bulkResults && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        <span className="font-semibold text-[#1A1F2E]">{bulkRows.length}</span>{' '}
                        row{bulkRows.length === 1 ? '' : 's'} parsed and ready to transfer
                      </div>
                      <Button
                        onClick={handleBulkTransfer}
                        disabled={bulkProcessing}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                        data-testid="bulk-start-transfer-btn"
                      >
                        {bulkProcessing ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Transferring {bulkRows.length} bundles…
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Start Bulk Transfer
                          </>
                        )}
                      </Button>
                    </div>

                    {bulkProcessing && (
                      <div className="text-sm text-gray-500 flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Processing bundles… please wait
                      </div>
                    )}

                    <div className="border rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                      <Table data-testid="bulk-preview-table">
                        <TableHeader className="bg-gray-50">
                          <TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Target Barcode</TableHead>
                            <TableHead>Bundle Code</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bulkRows.slice(0, 100).map((r, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-gray-500">{idx + 1}</TableCell>
                              <TableCell className="font-mono text-sm">{r.target_barcode}</TableCell>
                              <TableCell className="font-mono text-sm">{r.bundle_code}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {bulkRows.length > 100 && (
                        <div className="p-2 text-center text-xs text-gray-500 bg-gray-50">
                          Showing first 100 of {bulkRows.length} rows. All rows will be processed.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {bulkResults && (
                  <div className="space-y-3" data-testid="bulk-results-panel">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-600 font-medium">Total Rows</p>
                        <p className="text-2xl font-bold text-blue-800">{bulkResults.total_rows}</p>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <p className="text-xs text-emerald-600 font-medium">Transferred</p>
                        <p className="text-2xl font-bold text-emerald-800" data-testid="bulk-success-count">
                          {bulkResults.success_count}
                        </p>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-xs text-red-600 font-medium">Failed</p>
                        <p className="text-2xl font-bold text-red-800" data-testid="bulk-failure-count">
                          {bulkResults.failure_count}
                        </p>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-xs text-amber-600 font-medium">Pieces Updated</p>
                        <p className="text-2xl font-bold text-amber-800">{bulkResults.total_usns_transferred}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-[#1A1F2E]">Per-row Results</h4>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDownloadResults}
                          data-testid="bulk-download-results-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Results CSV
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleBulkClear}
                          data-testid="bulk-new-upload-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Upload Another
                        </Button>
                      </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                      <Table data-testid="bulk-results-table">
                        <TableHeader className="bg-gray-50 sticky top-0">
                          <TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Target Barcode</TableHead>
                            <TableHead>Bundle Code</TableHead>
                            <TableHead className="text-center">Pieces</TableHead>
                            <TableHead>Message</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bulkResults.results.map((r) => (
                            <TableRow key={r.row}>
                              <TableCell className="text-gray-500">{r.row}</TableCell>
                              <TableCell>
                                {r.success ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                    <Check className="w-3 h-3 mr-1" /> Transferred
                                  </Badge>
                                ) : (
                                  <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                                    <X className="w-3 h-3 mr-1" /> {r.error_code || 'failed'}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="font-mono text-sm">{r.target_barcode}</TableCell>
                              <TableCell className="font-mono text-sm">{r.bundle_code}</TableCell>
                              <TableCell className="text-center">
                                {r.success ? <Badge variant="outline">{r.usn_count}</Badge> : '-'}
                              </TableCell>
                              <TableCell className="text-xs text-gray-600 max-w-xs truncate" title={r.message}>
                                {r.success
                                  ? `${r.original_design || 'Unknown'} → ${r.target_design || ''}`
                                  : (r.message || '')}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <h3 className="font-semibold text-blue-800 mb-2">How Bulk Transfer Works</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
                  <li>Download the CSV template (columns: <code>target_barcode</code>, <code>bundle_code</code>)</li>
                  <li>Fill one row per bundle you want to transfer to a target SKU</li>
                  <li>Upload the CSV — rows will be previewed before processing</li>
                  <li>Click <strong>Start Bulk Transfer</strong>. Invalid rows are skipped and reported individually</li>
                  <li>Download the results CSV to verify each row's outcome</li>
                </ol>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Confirm Transfer Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-orange-600" />
              Confirm Design Transfer
            </DialogTitle>
            <DialogDescription>
              You are about to transfer all pieces in this bundle to a new design.
            </DialogDescription>
          </DialogHeader>
          
          {bundleDetails && targetSku && (
            <div className="space-y-4 py-4">
              {/* Bundle Info */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div>
                  <p className="text-xs text-gray-500">Bundle Code</p>
                  <p className="font-bold text-lg">{bundleDetails.bundle_code}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Total Pieces</p>
                    <p className="font-semibold text-2xl text-blue-600">{bundleDetails.usn_count}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Current Design</p>
                    <p className="font-medium text-red-600">{bundleDetails.current_design || 'Unknown'}</p>
                  </div>
                </div>
              </div>

              {/* Transfer Arrow */}
              <div className="flex items-center justify-center">
                <ArrowRightLeft className="w-8 h-8 text-orange-500" />
              </div>

              {/* Target Design */}
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-xs text-green-600">New Design</p>
                <p className="font-bold text-xl text-green-800">{targetSku.design_name}</p>
                <p className="text-sm text-green-600">{targetSku.shade} / {targetSku.size}</p>
              </div>

              {/* Warning if labels already printed */}
              {bundleDetails.label_printed && (
                <Alert className="bg-amber-50 border-amber-300">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700">
                    Labels have already been printed for this bundle. After transfer, labels will need to be reprinted.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isTransferring}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmTransfer}
              disabled={isTransferring}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="confirm-transfer-btn"
            >
              {isTransferring ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Transferring...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Transfer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Transfer History
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bundle</TableHead>
                  <TableHead>From Design</TableHead>
                  <TableHead>To Design</TableHead>
                  <TableHead className="text-center">Pieces</TableHead>
                  <TableHead>Transferred At</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transferHistory.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono font-medium">{item.bundle_code}</TableCell>
                    <TableCell className="text-red-600">{item.original_design || '-'}</TableCell>
                    <TableCell className="text-green-600 font-medium">{item.target_design}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{item.usn_count}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {new Date(item.transferred_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm">{item.transferred_by_name}</TableCell>
                  </TableRow>
                ))}
                {transferHistory.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                      No transfer history found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SFGDesignTransfer;
